/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IChat, IChatContent } from '../../pages/chat/type';
import { IChatState, IStatus, IIncomingRoom, IActiveRoom, IAction, IMessage } from "./types";

const addMessage = (state: IChatState, action: IMessage[]) => {
    action.forEach(msg => {
        if (state.messages.find(a => a.messageId == msg.messageId) == undefined)
            state.messages.push(msg);
    });
}
const removeExistingChatRoom = (state: IChatState, action: PayloadAction<IIncomingRoom[]>) => {
    state.incomingRoom.forEach((i, idx) => {
        if (action.payload.find((a) => a.roomId == i.roomId)) state.incomingRoom.splice(idx);
    })
}
const addNotification = (state: IChatState, payload: IMessage[]) => {
    payload.forEach(msg => {
        if (state.notifications.find(a => a.messageId == msg.messageId) == undefined)
            state.notifications.push(msg);
    });
}
const updateLatestMessage = (i: IIncomingRoom, action: IAction) => {
    const message = (action.payload.message as IMessage[])[0];
    if (message && i.roomId == message.roomId) {
        i.latestMessage = message.messageType == "TEXT"
            ? message.message : 'Send ' + message.messageType.toLocaleLowerCase();
        i.latestMessageTime = message.messageTime;
    }
    return i;
}
const updateIncomingRoom = (state: IChatState, action: PayloadAction<IStatus>) => {
    if (action.payload.status == "RESOLVED") {
        state.incomingRoom = state.incomingRoom.filter(i => i.sessionId != action.payload.sessionId);
    } else if (action.payload.status == "SENT") {
        state.incomingRoom = state.incomingRoom.map(i => updateLatestMessage(i, action));
    }
    else if (action.payload.status == "ONLINE" || action.payload.status == "OFFLINE") {
        state.incomingRoom = state.incomingRoom.map(i => {
            if (i.userName == action.payload.userName) i.status = action.payload.status.toLowerCase();
            return i;
        });
    }
}
const updateNotifications = (state: IChatState, action: PayloadAction<{ messages: IMessage[], userName: string }>) => {
    //set is sent flag     
    action.payload.messages.map(i => {
        i.isSent = true;
    });

    const incomingMessage = ([...action.payload.messages] as IMessage[]);
    const unreadNotif = incomingMessage.filter(a => !a.messageRead && a.senderName != action.payload.userName);
    //update notif from newly chat
    if (unreadNotif) {
        addNotification(state, unreadNotif);
    }
    //remove previous clicked notification
    if (state.activeRoom) {
        state.notifications.forEach((item, idx) => {
            if (item.sessionId == state.activeRoom.sessionId) {
                const exist = incomingMessage.find(a => a.messageId == item.messageId && a.messageRead != item.messageRead);
                if (exist) state.notifications.splice(idx);
            }
        });
    }
}

const resetUnreadMessage = (state: IChatState, sessionId: string) => {
    state.incomingRoom.forEach(a => {
        if (sessionId == a.sessionId) a.unreadMessageCount = 0
    });
}
const updateUnreadMessage = (state: IChatState, action: PayloadAction<{ messages: IMessage[], userName: string }>) => {
    state.incomingRoom.forEach(a => {
        if (a.sessionId == action.payload.messages[0]?.sessionId
            && action.payload.messages[0]?.senderName != action.payload.userName) {
            a.unreadMessageCount = Number(a.unreadMessageCount) + 1;
        }
        if (a.sessionId == state.activeRoom?.sessionId)
            a.unreadMessageCount = 0;
    });
}
const chatSlice = createSlice({
    name: 'chatAgent',
    initialState: {
        messages: [] as IMessage[],
        incomingRoom: [] as IIncomingRoom[],
        status: ({} as any) as IStatus,
        activeRoom: ({} as any) as IActiveRoom,
        notifications: [] as IMessage[],
        connected: false,
        scrollPosition: "bottom",
        layoutType: "Page",
        hideHeader: false,
        floatingRoomId: "",
        opponentFloating: ({} as any)
    },
    reducers: {
        incomingChatAgent: (state, action: PayloadAction<{ messages: IMessage[], userName: string }>) => {
            addMessage(state, action.payload.messages);
            updateNotifications(state, action);
            updateUnreadMessage(state, action);
        },
        IncomingChatAgentRoom: (state, action: PayloadAction<IIncomingRoom[]>) => {
            removeExistingChatRoom(state, action);
            state.incomingRoom = [...state.incomingRoom, ...action.payload];
        },
        updateChatAgentRoomOffset: (state, action: PayloadAction<{ sessionId: string; offset: number }>) => {
            const room = state.incomingRoom.find(a => a.sessionId == action.payload.sessionId);
            if (room) room.offset = action.payload.offset;
        },
        chatAgentCallBack: (state, action: PayloadAction<IStatus>) => {
            updateIncomingRoom(state, action);
            state.status = action.payload;
        },
        clearChatAgentCallBack: (state) => {
            state.status = {} as IStatus;
        },
        unreadAgentNotification: (state, action: PayloadAction<IMessage[]>) => {
            addNotification(state, action.payload);
        },
        activeChatAgentRoom: (state, action: PayloadAction<IActiveRoom>) => {
            resetUnreadMessage(state, action.payload.sessionId);
            state.activeRoom = action.payload;
        },
        connectedChatAgent: (state, action: PayloadAction<boolean>) => {
            state.connected = action.payload;
        },
        setChatAgentScrollPosition: (state, action: PayloadAction<"bottom" | "stay">) => {
            state.scrollPosition = action.payload;
        },
        removeAgentOfflineMessage: (state) => {
            state.messages = state.messages.filter(a => a.isSent);
        },
        resetChatAgentState: (state) => {
            state.incomingRoom = [];
            state.activeRoom = {} as IActiveRoom;
            state.notifications = [];
            state.messages = [];
        },
        sendAgentOfflineMessage: (state, action: PayloadAction<IChat>) => {
            const { sessionId, roomId, senderName, deviceId, content } = action.payload;
            const { files, message, type } = content;
            const newMessage = {
                message,
                messageFiles: files,
                messageId: 0,
                messageRead: false,
                messageType: type,
                roomId,
                sessionId,
                roomOwner: false,
                isSent: false,
                senderId: deviceId,
                senderName
            } as IMessage;
            state.messages = [...state.messages, newMessage];
        },
        chatAgentWSConnecting: (state) => {
            return;
        },
        chatAgentWSDisconnecting: (state) => {
            return;
        },
        onConnectedChat: (state, action: PayloadAction<{ deviceId: string, userName: string }>) => {
            return;
        },
        sendAgentMessage: (state, action: PayloadAction<IChat>) => {
            return;
        },
        resolveAgentRoom: (state, action: PayloadAction<{ sessionId: string }>) => {
            return;
        },
        readAgentMessage: (state, action: PayloadAction<{ sessionId: string | undefined, messageId: number }>) => {
            const unreads = state.notifications.filter(x => x.sessionId !== action.payload.sessionId);
            state.notifications = unreads;
        },
        refreshMessages: (state, action: PayloadAction<IMessage[]>) => {
            const messages = action.payload;

            state.notifications = messages;
        },
        typingAgentMessage: (state, action: PayloadAction<{ sessionId: string, userName: string }>) => {
            return;
        },
        startAgentChat: (state, action: PayloadAction<{ toUserName?: string, tenantId?: string }>) => {
            return;
        },
        startBroadcastAgentChat: (state, action: PayloadAction<{ content: IChatContent, toUserNames?: string[], deviceId?: string; tenantId?: string }>) => {
            return;
        },
        setLayoutType: (state, action: PayloadAction<"PAGE" | "LEFT_PANEL" | "FLOATING_PANEL">) => {
            state.layoutType = action.payload;
        },
        setHideHeader: (state, action: PayloadAction<boolean>) => {
            state.hideHeader = action.payload;
        },
        setFloatingRoomId(state, action: PayloadAction<string>) {
            state.floatingRoomId = action.payload;
        },
        setFloatingOpponent(state, action: PayloadAction<any>) {
            state.opponentFloating = action.payload;
        }

    },
});

export const {
    IncomingChatAgentRoom,
    activeChatAgentRoom,
    chatAgentCallBack,
    chatAgentWSConnecting,
    chatAgentWSDisconnecting,
    clearChatAgentCallBack,
    connectedChatAgent,
    incomingChatAgent,
    onConnectedChat,
    readAgentMessage,
    removeAgentOfflineMessage,
    resetChatAgentState,
    resolveAgentRoom,
    sendAgentMessage,
    sendAgentOfflineMessage,
    setChatAgentScrollPosition,
    typingAgentMessage,
    unreadAgentNotification,
    startAgentChat,
    updateChatAgentRoomOffset,
    refreshMessages,
    startBroadcastAgentChat,
    setLayoutType,
    setHideHeader,
    setFloatingRoomId,
    setFloatingOpponent
} = chatSlice.actions
export default chatSlice.reducer

